import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import { Button, Comment } from 'antd';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as CollapsedCommentSvg } from '@assets/icons/collapsed-comment.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as ExpandCommentSvg } from '@assets/icons/expand-comment.svg';
import { ReactComponent as ExpandVideoSvg } from '@assets/icons/expand-video.svg';
import { ReactComponent as CollapsedVideoSvg } from '@assets/icons/collapsed-video.svg';
import { useOutletContext } from 'react-router-dom';
import type { VideoJsPlayer } from 'video.js';
import {
  clampFloatCommentPosition,
  commentHasFloatAnchor,
  getDocumentHighlightViewportPosition,
  getFloatCanvasAnnotations,
  getFloatCommentViewportLayout,
  getNavigableAnnotationComments,
  resolveFloatRootComment
} from './floatCommentHelpers';
import { createNewComment } from '@redux/actions/mediaViewerAction';
import { useSelectedMVComment, useAssetMVPermissions } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { FabricContext } from '@context/FabricContext';
import CommentItem from '@pages/MediaViewer/Comments/components/CommentItem';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import CommentViewReplies from '@pages/MediaViewer/Comments/CommentViewReplies';
import {
  AssetItemDto,
  AssetVersionCommentDto,
  AssetVersionItemDto
} from '@api/Api';
import {
  MediaViewerOutletContextState,
  UpdateAssetCommentParams
} from '@layouts/types';
import { useAuth } from '@hooks/useAuth';
import { nonNull } from '@helpers/non-null';
import { useCreateTask } from '@hooks/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { openTaskModal } from '@redux/reducers/tasks';
import { buildCommentActions } from '@pages/MediaViewer/Comments/buildCommentActions';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import { useDeleteAttachment } from '@hooks/queries/attachment';
import {
  buildCreateCommentPayload,
  buildEditCommentPayload,
  createTurnCommentIntoTask,
  mapUppyFilesToAttachments
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import getObjectViewportPosition, {
  choosePosition,
  constrainPositionToWindow,
  getObjectViewportRect
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';
import { useResponsive } from '@hooks/useResponsive';
import ControlBarSlider from '@pages/MediaViewer/AssetViewer/VideoCard/components/Slider';
import { useVideoPlayerState } from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/hooks/useVideoPlayerState';
import {
  PIN_DRAW_TOOL,
  DEFAULT_DRAW_TOOL,
  isAnnotationDrawTool,
  isCommentFormOpen,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';
import './FloatCommentItem.less';
import { IS_UXP_WEBVIEW } from '@config/constants';

function FloatCommentVideoSlider({
  player,
  selectedVersion
}: {
  player: VideoJsPlayer;
  selectedVersion: AssetVersionItemDto;
}) {
  const { currentTime, duration, isExpand, buffered } =
    useVideoPlayerState(player);

  const onSliderChange = useCallback(
    (value: number) => {
      player.currentTime(value);
    },
    [player]
  );

  return (
    <ControlBarSlider
      asset={selectedVersion}
      isScrubsVisible={false}
      currentTime={currentTime}
      duration={duration}
      onSliderChange={onSliderChange}
      isExpand={isExpand}
      buffered={buffered}
    />
  );
}

type FloatCommentItemProps = {
  asset: AssetItemDto;
  selectedVersion: AssetVersionItemDto;
  commentMentions: boolean;
  canCreateTask: boolean;
  isShowVideo?: boolean;
  onExpandVideo?: (value: boolean) => void;
  setSelectedCollapse?: (collapse: string | null) => void;
};

function FloatCommentItem({
  asset,
  selectedVersion,
  commentMentions,
  canCreateTask,
  isShowVideo,
  onExpandVideo,
  setSelectedCollapse
}: FloatCommentItemProps) {
  const isEmbeddedWebView = IS_UXP_WEBVIEW;
  const { isMdWidth, isSmWidth, isLgWidth } = useResponsive();
  const {
    removeComment,
    setCommentResolve,
    setCommentLike,
    editComment,
    addNewComment,
    getPotentialMentions,
    getPotentialGroupMentions
  } = useOutletContext<MediaViewerOutletContextState>();
  const { user } = useAuth();
  const dispatch = useTypedDispatch();
  const createTask = useCreateTask();
  const canLeaveComment = useAssetMVPermissions('comment');
  const canLikeComment = useAssetMVPermissions('likeComments');
  const { canvas, clearCanvas } = useContext(FabricContext);
  const {
    selectedCommentId,
    commentsList,
    activePageNumber,
    setCanvasMode,
    setDocumentHighlight,
    selectComment,
    setShowReplyOfCommentId,
    setActivePageNumber,
    setDrawToolsShape,
    isHideComments,
    shapesDrawingTools,
    playerTime,
    getPlaybackPlayer
  } = useAssetViewerState();
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [contentMaxHeight, setContentMaxHeight] = useState<number | null>(null);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [expandedRepliesOfCommentId, setExpandedRepliesOfCommentId] = useState<
    string | null
  >(null);
  const [isCollapsedComment, setIsCollapsedComment] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);

  const [isDragging, setIsDragging] = useState(false);
  const frozenPositionRef = useRef<{ left: number; top: number } | null>(null);
  const isUserPositionedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const isSyncingLayoutRef = useRef(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    left: number;
    top: number;
  } | null>(null);
  const selectedCommentFromHook = useSelectedMVComment();
  const displayComment = useMemo(() => {
    if (replyToCommentId) {
      const replyTarget = commentsList.find((c) => c.id === replyToCommentId);
      return resolveFloatRootComment(replyTarget, commentsList) || replyTarget;
    }
    return resolveFloatRootComment(selectedCommentFromHook, commentsList);
  }, [replyToCommentId, commentsList, selectedCommentFromHook]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const uppy = useMVCommentAttachUppy(asset.id || '');
  const { mutateAsync: deleteAttachment } = useDeleteAttachment({});
  const { potentialMentions, potentialGroupMentions, setSearchQuery } =
    useCommentMentions({
      assetVersionId: selectedVersion?.id,
      getPotentialMentions,
      getPotentialGroupMentions
    });

  const isValidSelected = !!displayComment?.id && selectedCommentId !== 'new';
  const assetType = selectedVersion?.metadata.type;
  const isAudioVideo = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudioVideo && 'timeCode') || null;

  const hasAnnotatedComment = useMemo(
    () => (displayComment ? commentHasFloatAnchor(displayComment) : false),
    [displayComment]
  );

  const replyChildren = useMemo(() => {
    if (!displayComment?.id || displayComment.topParentId) return [];
    return commentsList
      .filter((comment) => comment.topParentId === displayComment.id)
      .sort(
        (a, b) =>
          new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf()
      );
  }, [commentsList, displayComment?.id, displayComment?.topParentId]);

  const repliesCount = replyChildren.length;
  const isRepliesExpanded =
    !!displayComment?.id && displayComment.id === expandedRepliesOfCommentId;

  const openReply = useCallback(
    (parentId: string) => {
      setEditCommentId(null);
      setDocumentHighlight(null);
      if (position) frozenPositionRef.current = position;
      setReplyToCommentId(parentId);
      selectComment('reply');
      clearCanvas();
      setCanvasMode('edit');
      setDrawToolsShape(PIN_DRAW_TOOL);
    },
    [
      position,
      selectComment,
      clearCanvas,
      setCanvasMode,
      setDocumentHighlight,
      setDrawToolsShape
    ]
  );

  const closeReply = useCallback(
    (parentId?: string) => {
      frozenPositionRef.current = null;
      const restoreId = parentId ?? replyToCommentId;
      setReplyToCommentId(null);
      setCanvasMode('view');
      setDrawToolsShape(DEFAULT_DRAW_TOOL);
      if (restoreId) {
        const restoreComment = commentsList.find((c) => c.id === restoreId);
        selectComment(restoreComment?.topParentId || restoreId);
      }
    },
    [
      commentsList,
      replyToCommentId,
      selectComment,
      setCanvasMode,
      setDrawToolsShape
    ]
  );

  const onShowReplyClick = useCallback(() => {
    if (!displayComment?.id) return;
    const rootId = displayComment.id;
    setExpandedRepliesOfCommentId((current) =>
      current === rootId ? null : rootId
    );
    if (replyToCommentId) {
      closeReply(rootId);
    }
  }, [closeReply, displayComment?.id, replyToCommentId]);

  const isFloatFormOpen = !!replyToCommentId || !!editCommentId;

  const updatePosition = useCallback(() => {
    if (!canvas || !isValidSelected || !displayComment?.id) return;
    if (isFloatFormOpen && frozenPositionRef.current) {
      setPosition(frozenPositionRef.current);
      return;
    }
    if (isUserPositionedRef.current) return;
    const canvasObjects = canvas
      .getObjects()
      .filter((o) => o.type !== 'highlight' && o.visible !== false);
    const activeObject = canvas.getActiveObject();
    const anchorObject =
      activeObject &&
      activeObject.type !== 'highlight' &&
      (activeObject as any).visible !== false
        ? activeObject
        : canvasObjects[0];

    if (anchorObject) {
      const initialPos = getObjectViewportPosition(canvas, anchorObject as any);
      const constrained = constrainPositionToWindow(initialPos);
      const objectRect = getObjectViewportRect(canvas, anchorObject as any);
      const finalPos =
        objectRect && constrained
          ? choosePosition(canvas, objectRect, constrained) ?? constrained
          : constrained;
      setPosition(finalPos);
      return;
    }

    const highlight = displayComment.documentHighlight;
    const rects = displayComment.documentHighlightRects;
    if (!highlight || !rects?.length) {
      if (isFloatFormOpen) return;
      setPosition(null);
      return;
    }
    setPosition(getDocumentHighlightViewportPosition(canvas, rects));
  }, [canvas, displayComment, isFloatFormOpen, isValidSelected]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('after:render', updatePosition);
    canvas.on('object:modified', updatePosition);
    canvas.on('object:moving', updatePosition);
    canvas.on('selection:updated', updatePosition);
    canvas.on('selection:created', updatePosition);
    canvas.on('selection:cleared', updatePosition);
    return () => {
      canvas.off('after:render', updatePosition);
      canvas.off('object:modified', updatePosition);
      canvas.off('object:moving', updatePosition);
      canvas.off('selection:updated', updatePosition);
      canvas.off('selection:created', updatePosition);
      canvas.off('selection:cleared', updatePosition);
    };
  }, [canvas, updatePosition]);

  const navigableComments = useMemo(
    () =>
      getNavigableAnnotationComments(
        commentsList,
        assetType,
        isDocument ? activePageNumber ?? 1 : undefined
      ),
    [commentsList, assetType, isDocument, activePageNumber]
  );

  const isCommentOnActivePage = useMemo(() => {
    if (!isDocument || !displayComment) return true;
    if (displayComment.documentPage == null) return true;
    return displayComment.documentPage === activePageNumber;
  }, [isDocument, displayComment, activePageNumber]);

  const annotationNavigationId = useMemo(() => {
    if (!displayComment?.id) return null;
    return displayComment.topParentId || displayComment.id;
  }, [displayComment?.id, displayComment?.topParentId]);

  const currentAnnotationIndex = useMemo(() => {
    if (!annotationNavigationId) return -1;
    return navigableComments.findIndex((c) => c.id === annotationNavigationId);
  }, [annotationNavigationId, navigableComments]);

  const selectNavigableComment = useCallback(
    (comment: AssetVersionCommentDto) => {
      frozenPositionRef.current = null;
      isUserPositionedRef.current = false;
      setEditCommentId(null);
      setReplyToCommentId(null);
      setExpandedRepliesOfCommentId(null);
      selectComment(comment.id);
      setCanvasMode('view');
      if (comment.documentPage != null) {
        setActivePageNumber(comment.documentPage);
      }
      setDocumentHighlight(
        comment.documentHighlight
          ? {
              text: comment.documentHighlight,
              rects: comment.documentHighlightRects
            }
          : null
      );
    },
    [
      selectComment,
      setActivePageNumber,
      setCanvasMode,
      setDocumentHighlight,
      setEditCommentId
    ]
  );

  useEffect(() => {
    if (
      !selectedCommentId ||
      selectedCommentId === 'new' ||
      selectedCommentId === 'reply'
    ) {
      return;
    }

    const selected = commentsList.find(
      (comment) => comment.id === selectedCommentId
    );
    const selectedRootId = selected?.topParentId || selectedCommentId;

    if (replyToCommentId) {
      const replyRootId =
        commentsList.find((comment) => comment.id === replyToCommentId)
          ?.topParentId || replyToCommentId;
      if (replyRootId !== selectedRootId) {
        frozenPositionRef.current = null;
        setReplyToCommentId(null);
        setCanvasMode('view');
        setDrawToolsShape(DEFAULT_DRAW_TOOL);
      }
    }

    if (editCommentId) {
      const editRootId =
        commentsList.find((comment) => comment.id === editCommentId)
          ?.topParentId || editCommentId;
      if (editRootId !== selectedRootId) {
        setEditCommentId(null);
      }
    }

    if (
      expandedRepliesOfCommentId &&
      expandedRepliesOfCommentId !== selectedRootId
    ) {
      setExpandedRepliesOfCommentId(null);
    }
  }, [
    commentsList,
    editCommentId,
    expandedRepliesOfCommentId,
    replyToCommentId,
    selectedCommentId,
    setCanvasMode,
    setDrawToolsShape
  ]);

  const onClick = useCallback(() => {
    if (!displayComment) return;
    if (displayComment.isCopied) return;
    selectNavigableComment(displayComment);
  }, [displayComment, selectNavigableComment]);

  const onNavigateAnnotation = useCallback(
    (direction: -1 | 1) => {
      if (!navigableComments.length) return;
      const currentIndex =
        currentAnnotationIndex >= 0 ? currentAnnotationIndex : 0;
      const nextIndex =
        (currentIndex + direction + navigableComments.length) %
        navigableComments.length;
      selectNavigableComment(navigableComments[nextIndex]);
    },
    [currentAnnotationIndex, navigableComments, selectNavigableComment]
  );

  const onResetAsset = useCallback(() => {
    frozenPositionRef.current = null;
    isUserPositionedRef.current = false;
    selectComment(null);
    setCanvasMode('view');
    setDocumentHighlight(null);
    setEditCommentId(null);
    setReplyToCommentId(null);
    setExpandedRepliesOfCommentId(null);
    if (shapesDrawingTools.shape !== CURSOR_DRAW_TOOL) {
      setDrawToolsShape(DEFAULT_DRAW_TOOL);
    }
    setIsCollapsedComment(false);
    onExpandVideo?.(false);
    setPosition(null);
  }, [
    selectComment,
    setCanvasMode,
    setDocumentHighlight,
    setEditCommentId,
    setDrawToolsShape,
    setIsCollapsedComment,
    onExpandVideo,
    setPosition,
    shapesDrawingTools.shape
  ]);

  const getAnnotations = useCallback((): string | null => {
    if (!canvas) return null;
    return getFloatCanvasAnnotations(canvas);
  }, [canvas]);

  const onAddReply = useCallback(
    async (values: SubmitEditorValues) => {
      const parentId = values.idOrParentId;
      const parentComment = commentsList.find((it) => it.id === parentId);
      const data = buildCreateCommentPayload({
        values,
        selectedVersionId: selectedVersion?.id || '',
        isCheckboxChecked,
        isAudioVideo,
        isDocument,
        playerTime,
        activePageNumber,
        annotations: getAnnotations(),
        parentComment
      });
      const comment = await addNewComment(data, parentId);
      const files = uppy.getFiles();
      if (files.length) {
        files.forEach((item) => {
          uppy.setFileMeta(item.id, {
            commentId: comment.id,
            readyForUpload: true
          });
        });
        await uppy.upload();
      }
      const replyParentId = parentComment?.topParentId || parentId;
      await createNewComment(
        {
          ...comment,
          attachments: mapUppyFilesToAttachments(files),
          topParentId: comment.topParentId || replyParentId,
          repliedToId: comment.repliedToId || parentId
        },
        selectedVersion?.id || ''
      );
      clearCanvas();
      setDrawToolsShape(DEFAULT_DRAW_TOOL);
      setExpandedRepliesOfCommentId(replyParentId);
      setShowReplyOfCommentId(replyParentId);
      closeReply();
      if (isLgWidth) {
        setSelectedCollapse?.('comments');
      }
      selectComment(comment.id);
    },
    [
      addNewComment,
      activePageNumber,
      clearCanvas,
      closeReply,
      commentsList,
      isCheckboxChecked,
      getAnnotations,
      isAudioVideo,
      isDocument,
      isLgWidth,
      playerTime,
      selectedVersion?.id,
      selectComment,
      setDrawToolsShape,
      setSelectedCollapse,
      setShowReplyOfCommentId,
      uppy
    ]
  );

  const onDeleteComment = useCallback(
    async (id: string) => {
      await removeComment(id);
      if (!displayComment || id === displayComment.id) {
        onResetAsset();
        return;
      }
      if (selectedCommentId === id) {
        selectComment(displayComment.id);
      }
    },
    [
      displayComment,
      onResetAsset,
      removeComment,
      selectComment,
      selectedCommentId
    ]
  );

  const onResolveClick = useCallback(
    async (comment: AssetVersionCommentDto) => {
      if (!user) return;
      const {
        id: userId,
        type,
        pictureUrl,
        email,
        firstName,
        lastName
      } = nonNull(user);
      await setCommentResolve(
        { id: comment.id, value: !comment.resolvedAt } as any,
        {
          id: userId,
          type: type === 'internal' ? 'user' : 'external-reviewer',
          picture: { url: pictureUrl ?? null },
          email,
          firstName,
          lastName: lastName || '',
          me: true
        }
      );
    },
    [setCommentResolve, user]
  );

  const onLikeClick = useCallback(
    async (comment: AssetVersionCommentDto) => {
      if (!user) return;
      const hasLike = comment.likes.find((el: any) => el.me);
      const {
        id: userId,
        type,
        pictureUrl,
        email,
        firstName,
        lastName
      } = nonNull(user);
      await setCommentLike({ id: comment.id, value: !hasLike } as any, {
        id: userId,
        type: type === 'internal' ? 'user' : 'external-reviewer',
        picture: { url: pictureUrl ?? null },
        email,
        firstName,
        lastName: lastName || '',
        me: true
      });
    },
    [setCommentLike, user]
  );

  const onExpandCommentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsedComment((prevIsCollapsedComment) => !prevIsCollapsedComment);
  }, []);

  const onExpandVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpandVideo?.(!isShowVideo);
  };

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch,
    isOpenTaskModal: !isSmWidth,
    setSelectedCollapse,
    openTasksInSidebar: isLgWidth
  });

  const onEditComment = useCallback(
    async ({
      text,
      rawText,
      mentions,
      groupMentions,
      idOrParentId: id,
      documentHighlight,
      documentHighlightRects,
      documentHighlightReplacement,
      attachmentsLeft
    }: SubmitEditorValues) => {
      const commentToEdit =
        commentsList.find((comment) => comment.id === id) || displayComment;
      if (!commentToEdit) return;
      const data: UpdateAssetCommentParams = {
        ...buildEditCommentPayload(
          {
            text,
            rawText,
            mentions,
            groupMentions,
            idOrParentId: id,
            documentHighlight,
            documentHighlightRects,
            documentHighlightReplacement,
            attachmentsLeft
          },
          commentToEdit
        ),
        annotations:
          commentToEdit.id === displayComment?.id ? getAnnotations() : undefined
      } as UpdateAssetCommentParams;

      const files = uppy.getFiles();
      const attachmentIdsLeft = (attachmentsLeft || []).map((item) => item.id);
      const removeFilesPromise = (commentToEdit.attachments || [])
        .filter((attachment) => !attachmentIdsLeft.includes(attachment.id))
        .map((attachment) =>
          deleteAttachment({
            id: attachment.id
          })
        );
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: commentToEdit.id,
          readyForUpload: true
        });
      });
      if (removeFilesPromise.length) await Promise.all(removeFilesPromise);
      if (files.length) await uppy.upload();

      await editComment(data);
      setEditCommentId(null);
      if (commentToEdit.id === displayComment?.id) {
        onResetAsset();
      } else {
        setCanvasMode('view');
        setDrawToolsShape(DEFAULT_DRAW_TOOL);
      }
    },
    [
      commentsList,
      deleteAttachment,
      displayComment,
      editComment,
      getAnnotations,
      onResetAsset,
      setCanvasMode,
      setDrawToolsShape,
      uppy
    ]
  );

  const shouldShow = useMemo(
    () =>
      isValidSelected &&
      !displayComment?.isCopied &&
      hasAnnotatedComment &&
      isCommentOnActivePage &&
      !!displayComment?.id &&
      (!!position || isFloatFormOpen || isMdWidth),
    [
      displayComment?.id,
      displayComment?.isCopied,
      hasAnnotatedComment,
      isCommentOnActivePage,
      isFloatFormOpen,
      isMdWidth,
      isValidSelected,
      position
    ]
  );

  const clampToViewport = useCallback(
    (pos: { left: number; top: number }) => {
      const el = containerRef.current;
      if (!el) return pos;
      return clampFloatCommentPosition(pos, {
        width: el.offsetWidth || 0,
        height: Math.min(
          el.offsetHeight || 0,
          contentMaxHeight || el.offsetHeight || 0
        )
      });
    },
    [contentMaxHeight]
  );

  const syncFloatViewportLayout = useCallback(() => {
    if (isMdWidth || !position || !containerRef.current) return;
    if (isSyncingLayoutRef.current || isDraggingRef.current) return;

    isSyncingLayoutRef.current = true;
    const el = containerRef.current;
    const previousMaxHeight = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const naturalHeight = el.scrollHeight;
    const naturalWidth = el.offsetWidth || 315;
    el.style.maxHeight = previousMaxHeight;

    const layout = getFloatCommentViewportLayout(
      position,
      naturalHeight,
      naturalWidth
    );

    const nextMaxHeight =
      naturalHeight > layout.maxHeight ? layout.maxHeight : null;

    setContentMaxHeight((prev) =>
      prev === nextMaxHeight ? prev : nextMaxHeight
    );

    if (layout.left !== position.left || layout.top !== position.top) {
      const nextPos = { left: layout.left, top: layout.top };
      if (frozenPositionRef.current) {
        frozenPositionRef.current = nextPos;
      }
      setPosition(nextPos);
    }

    requestAnimationFrame(() => {
      isSyncingLayoutRef.current = false;
    });
  }, [isMdWidth, position]);

  useLayoutEffect(() => {
    if (!shouldShow || isMdWidth || !position) return;
    syncFloatViewportLayout();
  }, [
    shouldShow,
    isMdWidth,
    position?.left,
    position?.top,
    replyToCommentId,
    isRepliesExpanded,
    editCommentId,
    repliesCount,
    syncFloatViewportLayout
  ]);

  useEffect(() => {
    if (!shouldShow || isMdWidth) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      syncFloatViewportLayout();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldShow, isMdWidth, syncFloatViewportLayout]);

  const onCommentMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMdWidth || !position) return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(
          'button, a, input, textarea, select, [contenteditable="true"], .ant-dropdown'
        )
      ) {
        return;
      }

      isPointerDownRef.current = true;
      hasDraggedRef.current = false;
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        left: position.left,
        top: position.top
      };
    },
    [isMdWidth, position]
  );

  const onCommentClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDraggedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isMdWidth) return undefined;

    const onMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;

      if (!isDraggingRef.current && isPointerDownRef.current) {
        const deltaX = e.clientX - dragStartRef.current.mouseX;
        const deltaY = e.clientY - dragStartRef.current.mouseY;
        if (Math.hypot(deltaX, deltaY) < 5) return;

        isDraggingRef.current = true;
        hasDraggedRef.current = true;
        setIsDragging(true);
        isUserPositionedRef.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }

      if (!isDraggingRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.mouseX;
      const deltaY = e.clientY - dragStartRef.current.mouseY;
      setPosition(
        clampToViewport({
          left: dragStartRef.current.left + deltaX,
          top: dragStartRef.current.top + deltaY
        })
      );
    };

    const onMouseUp = () => {
      isPointerDownRef.current = false;
      dragStartRef.current = null;

      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isMdWidth, clampToViewport]);

  useEffect(() => {
    if (!shouldShow) return;
    if (isMdWidth) return;
    const clearSelectedComment = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (isCommentFormOpen(selectedCommentId)) return;
      if (containerRef.current?.contains(target)) return;
      if (target.closest('.float_comment_editor')) return;
      if (target.closest('.media_viewer_menu')) return;
      if (target.closest('.shapes_drawing_tools_container')) return;
      if (target.closest('.compare_version_tools_container')) return;
      if (target.closest('.comment_mentions')) return;
      const modalWrap = target.closest<HTMLElement>('.ant-modal-wrap');
      if (
        modalWrap &&
        !modalWrap.classList.contains('compare-versions-modal')
      ) {
        return;
      }
      if (
        displayComment?.timeCode?.toSeconds &&
        (target.closest('.video_control_bar_container') ||
          target.closest('.video_control_bar_track'))
      ) {
        return;
      }
      if (isAnnotationDrawTool(shapesDrawingTools.shape)) return;
      onResetAsset();
    };
    document.addEventListener('click', clearSelectedComment);
    return () => document.removeEventListener('click', clearSelectedComment);
  }, [
    shouldShow,
    selectedCommentId,
    shapesDrawingTools.shape,
    onResetAsset,
    isMdWidth,
    displayComment?.timeCode?.toSeconds
  ]);

  useEffect(() => {
    if (!position || isMdWidth) return;
    const onResize = () => {
      syncFloatViewportLayout();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, isMdWidth, syncFloatViewportLayout]);

  const seekAndPause = useCallback((player: VideoJsPlayer, time: number) => {
    const wasMuted = player.muted();

    const onSeeked = () => {
      player.off('seeked', onSeeked);
      player.pause();
      player.muted(wasMuted);
    };

    const doSeek = () => {
      player.off('loadedmetadata', doSeek);
      player.currentTime(time);
      player.one('seeked', onSeeked);
    };

    player.muted(true);
    if (player.readyState() >= 1) {
      doSeek();
    } else {
      player.one('loadedmetadata', doSeek);
    }
    Promise.resolve(player.play()).catch(() => {
      player.off('loadedmetadata', doSeek);
      player.off('seeked', onSeeked);
      player.muted(wasMuted);
      player.pause();
    });

    return () => {
      player.off('loadedmetadata', doSeek);
      player.off('seeked', onSeeked);
    };
  }, []);

  useEffect(() => {
    if (!isAudioVideo) return undefined;
    const player = getPlaybackPlayer();
    if (!player) return undefined;
    const fromSeconds = displayComment?.timeCode?.fromSeconds;
    if (typeof fromSeconds !== 'number') return undefined;

    return seekAndPause(player, fromSeconds);
  }, [
    displayComment?.id,
    displayComment?.timeCode?.fromSeconds,
    getPlaybackPlayer,
    isAudioVideo,
    seekAndPause
  ]);

  if (isHideComments) return null;
  if (!shouldShow || !displayComment) return null;

  const playbackPlayer = getPlaybackPlayer();

  const actions = buildCommentActions({
    comment: displayComment as any,
    taskId: displayComment.task?.id || null,
    canLeaveComment,
    canLikeComment,
    onOpenTask: () => {
      if (!displayComment.task?.id) return;
      dispatch(
        openTaskModal({
          taskId: displayComment.task.id,
          descriptionPlaceholder:
            'Describe your task so your teammates know what to do.'
        })
      );
    },
    onReply: () => {
      const parentId = displayComment.id;
      if (replyToCommentId === parentId) {
        closeReply(parentId);
        return;
      }
      openReply(parentId);
    },
    onLikeClick: () => onLikeClick(displayComment),
    isEmbeddedWebView
  });

  if (isMdWidth) {
    actions.push(
      <div
        key="float-comment-expand"
        className="float_comment_item_expand_actions"
      >
        <button
          type="button"
          className="float_comment_item_expand_btn"
          onClick={onExpandCommentClick}
        >
          <ExpandCommentSvg width={24} height={24} />
        </button>
        {isAudioVideo && (
          <button
            type="button"
            className="float_comment_item_expand_btn"
            onClick={onExpandVideoClick}
          >
            {isShowVideo ? (
              <CollapsedVideoSvg width={24} height={24} />
            ) : (
              <ExpandVideoSvg width={24} height={24} />
            )}
          </button>
        )}
      </div>
    );
  }

  const showMobileNavigation =
    isMdWidth &&
    isCommentOnActivePage &&
    navigableComments.length > 0 &&
    currentAnnotationIndex >= 0;

  const isShowVideoSlider =
    isMdWidth && !isShowVideo && isAudioVideo && playbackPlayer;

  const isCollapsed = isCollapsedComment && isMdWidth;

  return (
    <>
      {showMobileNavigation && (
        <div className="float_comment_item_head">
          <div className="float_comment_item_head-btn">
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              onClick={(e) => {
                e.stopPropagation();
                onResetAsset();
              }}
            >
              <ArrowLeftSvg />
            </Button>
          </div>
          Annotation {currentAnnotationIndex + 1} of {navigableComments.length}
        </div>
      )}
      {isCollapsed && (
        <div className="float_comment_item_collapsed">
          <Button
            htmlType="button"
            className="media_viewer_btn"
            onClick={onExpandCommentClick}
          >
            Expand comment
            <CollapsedCommentSvg
              className="b-white-icon"
              style={{ marginLeft: 8 }}
            />
          </Button>
          <Button
            htmlType="button"
            className="media_viewer_btn b-only-icon"
            style={{
              transform: 'rotate(180deg)',
              transformOrigin: 'center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateAnnotation(1);
            }}
          >
            <DownArrowSvg className="b-white-icon" width={18} height={18} />
          </Button>
          <Button
            htmlType="button"
            className="media_viewer_btn b-only-icon"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateAnnotation(-1);
            }}
          >
            <DownArrowSvg className="b-white-icon" width={18} height={18} />
          </Button>
        </div>
      )}
      {showMobileNavigation && !isCollapsed && (
        <div
          className="float_comment_item_nav"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Button
            htmlType="button"
            className="media_viewer_btn b-only-icon"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateAnnotation(-1);
            }}
          >
            <DownArrowSvg className="b-white-icon" width={18} height={18} />
          </Button>
          <Button
            htmlType="button"
            className="media_viewer_btn b-only-icon"
            style={{
              transform: 'rotate(180deg)',
              transformOrigin: 'center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateAnnotation(1);
            }}
          >
            <DownArrowSvg className="b-white-icon" width={18} height={18} />
          </Button>
        </div>
      )}
      {!isCollapsed && (
        <div
          ref={containerRef}
          className={classNames(
            'float_comment_item',
            replyToCommentId === displayComment.id && 'is_show_reply_form',
            isRepliesExpanded && repliesCount > 0 && 'has_scrollable_replies',
            isDragging && 'is_dragging'
          )}
          style={
            isMdWidth
              ? undefined
              : {
                  left: position!.left,
                  top: position!.top,
                  ...(contentMaxHeight
                    ? { maxHeight: contentMaxHeight }
                    : undefined)
                }
          }
          onMouseDown={!isMdWidth ? onCommentMouseDown : undefined}
          onClickCapture={!isMdWidth ? onCommentClickCapture : undefined}
        >
          {isShowVideoSlider && (
            <FloatCommentVideoSlider
              player={playbackPlayer}
              selectedVersion={selectedVersion}
            />
          )}
          <Comment
            actions={actions}
            content={
              editCommentId === displayComment.id ? (
                <CommentEditor
                  commentMentions={commentMentions}
                  comment={displayComment as any}
                  editorFor="edit"
                  editCommentId={displayComment.id}
                  setEditCommentId={setEditCommentId}
                  replyToCommentId={null}
                  setReplyToCommentId={setReplyToCommentId}
                  isFocused={true}
                  onSubmitEditor={onEditComment}
                  isCheckboxChecked={isCheckboxChecked}
                  setIsCheckboxChecked={setIsCheckboxChecked}
                  checkboxValueType={checkboxValueType}
                  potentialMentions={potentialMentions}
                  potentialGroupMentions={potentialGroupMentions}
                  searchPotentialMentions={(v) => setSearchQuery(v)}
                  uppy={uppy}
                />
              ) : (
                <CommentItem
                  comment={displayComment}
                  setEditCommentId={(id) => {
                    setEditCommentId(id);
                    setReplyToCommentId(null);
                  }}
                  setReplyToCommentId={(replyId) => {
                    if (!replyId) return;
                    openReply(replyId);
                  }}
                  onDeleteComment={onDeleteComment}
                  onTurnCommentIntoTask={onTurnCommentIntoTask}
                  onResolveClick={onResolveClick}
                  onClickComment={onClick}
                  canCreateTask={canCreateTask}
                  isEmbeddedWebView={isEmbeddedWebView}
                />
              )
            }
          >
            <>
              {replyToCommentId === displayComment.id && (
                <CommentEditor
                  commentMentions={commentMentions}
                  comment={displayComment as any}
                  editorFor="reply"
                  editCommentId={null}
                  setEditCommentId={setEditCommentId}
                  replyToCommentId={replyToCommentId}
                  setReplyToCommentId={(id) => {
                    if (!id) closeReply();
                  }}
                  isFocused={true}
                  onSubmitEditor={onAddReply}
                  isCheckboxChecked={isCheckboxChecked}
                  setIsCheckboxChecked={setIsCheckboxChecked}
                  checkboxValueType={checkboxValueType}
                  potentialMentions={potentialMentions}
                  potentialGroupMentions={potentialGroupMentions}
                  searchPotentialMentions={(v) => setSearchQuery(v)}
                  uppy={uppy}
                />
              )}
              {repliesCount > 0 && (
                <CommentViewReplies
                  count={repliesCount}
                  isExpanded={isRepliesExpanded}
                  onClick={onShowReplyClick}
                />
              )}
              {isRepliesExpanded && repliesCount > 0 && (
                <div className="float_comment_item_replies">
                  {replyChildren.map((replyComment) => {
                    const replyActions = buildCommentActions({
                      comment: replyComment as any,
                      taskId: replyComment.task?.id || null,
                      canLeaveComment,
                      canLikeComment,
                      onOpenTask: () => {
                        if (!replyComment.task?.id) return;
                        dispatch(
                          openTaskModal({
                            taskId: replyComment.task.id,
                            descriptionPlaceholder:
                              'Describe your task so your teammates know what to do.'
                          })
                        );
                      },
                      onReply: () => {
                        if (replyToCommentId === replyComment.id) {
                          closeReply(replyComment.id);
                          return;
                        }
                        openReply(replyComment.id);
                      },
                      onLikeClick: () => onLikeClick(replyComment)
                    });

                    return (
                      <Comment
                        key={replyComment.id}
                        actions={replyActions}
                        className={classNames(
                          (replyComment.id === selectedCommentId ||
                            replyComment.id === replyToCommentId) &&
                            'selected_comment',
                          replyComment.id === replyToCommentId &&
                            'is_show_reply_form'
                        )}
                        content={
                          editCommentId === replyComment.id ? (
                            <CommentEditor
                              commentMentions={commentMentions}
                              comment={replyComment as any}
                              editorFor="edit"
                              editCommentId={replyComment.id}
                              setEditCommentId={setEditCommentId}
                              replyToCommentId={replyToCommentId}
                              setReplyToCommentId={setReplyToCommentId}
                              isFocused={true}
                              onSubmitEditor={onEditComment}
                              isCheckboxChecked={isCheckboxChecked}
                              setIsCheckboxChecked={setIsCheckboxChecked}
                              checkboxValueType={checkboxValueType}
                              potentialMentions={potentialMentions}
                              potentialGroupMentions={potentialGroupMentions}
                              searchPotentialMentions={(v) => setSearchQuery(v)}
                              uppy={uppy}
                            />
                          ) : (
                            <CommentItem
                              comment={replyComment}
                              setEditCommentId={(id) => {
                                if (position) {
                                  frozenPositionRef.current = position;
                                }
                                setEditCommentId(id);
                                setReplyToCommentId(null);
                              }}
                              setReplyToCommentId={(replyId) => {
                                if (!replyId) return;
                                openReply(replyId);
                              }}
                              onDeleteComment={onDeleteComment}
                              onTurnCommentIntoTask={onTurnCommentIntoTask}
                              onResolveClick={onResolveClick}
                              onClickComment={() => {
                                if (replyComment.isCopied) return;
                                setEditCommentId(null);
                                setReplyToCommentId(null);
                                selectComment(replyComment.id);
                                setCanvasMode('view');
                                setDocumentHighlight(null);
                              }}
                              canCreateTask={canCreateTask}
                            />
                          )
                        }
                      >
                        {replyToCommentId === replyComment.id && (
                          <CommentEditor
                            commentMentions={commentMentions}
                            comment={replyComment as any}
                            editorFor="reply"
                            editCommentId={null}
                            setEditCommentId={setEditCommentId}
                            replyToCommentId={replyToCommentId}
                            setReplyToCommentId={(id) => {
                              if (!id) closeReply();
                            }}
                            isFocused={true}
                            onSubmitEditor={onAddReply}
                            isCheckboxChecked={isCheckboxChecked}
                            setIsCheckboxChecked={setIsCheckboxChecked}
                            checkboxValueType={checkboxValueType}
                            potentialMentions={potentialMentions}
                            potentialGroupMentions={potentialGroupMentions}
                            searchPotentialMentions={(v) => setSearchQuery(v)}
                            uppy={uppy}
                          />
                        )}
                      </Comment>
                    );
                  })}
                </div>
              )}
            </>
          </Comment>
        </div>
      )}
    </>
  );
}

export default FloatCommentItem;
